import type { State } from '../../State';
import { duration } from '../../utils/duration';
import { shuffleArray } from '../../utils/random';
import { getJSTDaysDiff } from '../../utils/time';
import {
  type PromoGroupConfig,
  type PromoGroupID,
  type PromoGroupItem,
  type PromoGroupItemID,
} from './promoGroup.types';
import { promoGroupConfig } from './promoGroup.ruleset';

/**
 * Returns how many times an item has been shown.
 */
export function getGroupItemShownCount(
  state: State,
  item: PromoGroupItem,
): number {
  const { groupId, itemId } = item;
  return state.promoGroups?.[groupId]?.items?.[itemId]?.count ?? 0;
}

/**
 * Returns all eligible items in the group, ordered by priority.
 *
 * Each item is checked independently: showOnce, eventID, per-item cooldown.
 * Items that have already been shown this cooldown period are excluded.
 * Groups using configured priority preserve ruleset order. Groups using
 * never-seen-first priority boost unseen items above previously shown items.
 *
 * Spec §4 Priority Rule #1 (FTUE): among multiple never-seen items, the
 * one with the earliest `eventStartAt` wins. Ties are broken randomly.
 * Items without an `eventStartAt` (e.g. showOnce Info dialogs) sort AFTER
 * items with one. Their ties preserve ruleset order unless the group opts
 * into randomizing undated ties.
 *
 * Trigger coordinators iterate this list and select the first items with
 * suitable frontend surfaces. After `groupItemWasResolved` records an item,
 * later launch triggers in the same day skip it.
 */
export function getReadyGroupItems<GroupID extends PromoGroupID>(
  state: State,
  groupId: GroupID,
  now: number,
): PromoGroupItemID<GroupID>[] {
  const config: PromoGroupConfig<GroupID> = promoGroupConfig[groupId];

  const boosted: {
    id: PromoGroupItemID<GroupID>;
    startAt: number | undefined;
  }[] = [];
  const candidates: PromoGroupItemID<GroupID>[] = [];
  const priorityMode = config.priorityMode ?? 'neverSeenFirst';

  for (const item of config.items) {
    if (!isItemEligible(state, groupId, item, config.cooldown, now)) continue;

    // Configured groups preserve the ruleset's fixed item order.
    if (priorityMode === 'configured') {
      candidates.push(item.id);
      continue;
    }

    // Never-seen-first groups separate unseen items for FTUE ordering.
    const itemCount =
      state.promoGroups?.[groupId]?.items?.[item.id]?.count ?? 0;

    if (itemCount === 0) {
      boosted.push({
        id: item.id,
        startAt: item.eventStartAt?.(state, now),
      });
    } else {
      candidates.push(item.id);
    }
  }

  // Fixed-priority groups need no secondary ordering pass.
  if (priorityMode === 'configured') return candidates;

  // Sort never-seen items per spec §4 Priority Rule #1.
  // Use a stable sort (Array.sort is stable in modern JS), then within
  // same-startAt groups apply a random shuffle for the spec's "do it randomly"
  // tiebreak.
  boosted.sort((a, b) => {
    // Items with eventStartAt come before items without.
    if (a.startAt !== undefined && b.startAt === undefined) return -1;
    if (a.startAt === undefined && b.startAt !== undefined) return 1;
    // Both have startAt: earliest wins.
    if (a.startAt !== undefined && b.startAt !== undefined) {
      return a.startAt - b.startAt;
    }
    // Neither has startAt: keep array order (stable).
    return 0;
  });

  // Randomize dated ties and configured undated FTUE ties.
  for (let i = 0; i < boosted.length;) {
    let j = i + 1;
    while (j < boosted.length && boosted[j].startAt === boosted[i].startAt) {
      j++;
    }
    const shouldRandomize =
      boosted[i].startAt !== undefined || config.randomizeUndatedTies;
    if (j - i > 1 && shouldRandomize) {
      // Shuffle the tied range with the shared Fisher-Yates implementation.
      const randomizedItems = boosted.slice(i, j);
      shuffleArray(randomizedItems, Math.random);

      // Replace the tied range while preserving the surrounding priority order.
      boosted.splice(i, randomizedItems.length, ...randomizedItems);
    }
    i = j;
  }

  return [...boosted.map((b) => b.id), ...candidates];
}

/**
 * Returns whether a specific item is eligible to be shown.
 *
 * Checks only this item's own state: showOnce, eventID recurrence,
 * and per-item cooldown (JST calendar day). Does NOT compare against
 * other items — the ready-list order handles priority.
 */
export function isGroupItemReady(
  state: State,
  item: PromoGroupItem,
  now: number,
): boolean {
  const { groupId, itemId } = item;
  const config: PromoGroupConfig = promoGroupConfig[groupId];

  const itemConfig = config.items.find((item) => item.id === itemId);
  if (!itemConfig) return false;

  return isItemEligible(state, groupId, itemConfig, config.cooldown, now);
}

/**
 * Checks if an individual item passes eligibility filters:
 * - showOnce: already shown once → blocked forever
 * - eventID: no active event or same event instance → blocked
 * - cooldown: still inside the item's cooldown, or the group's when the item
 *   has none → blocked
 */
function isItemEligible(
  state: State,
  groupId: PromoGroupID,
  item: PromoGroupConfig['items'][number],
  cooldown: number,
  now: number,
): boolean {
  const itemState = state.promoGroups?.[groupId]?.items?.[item.id];
  const itemCount = itemState?.count ?? 0;

  if (item.showOnce && itemCount >= 1) return false;

  if (item.eventID) {
    const currentEventId = item.eventID(state, now);
    if (currentEventId === undefined) return false;
    if (itemState?.lastShownEventId === currentEventId) return false;
  }

  // Items with their own cooldown ignore the group default.
  const itemCooldown = item.cooldown?.(state, now) ?? cooldown;

  const itemShownAt = itemState?.shownAt ?? 0;
  if (itemShownAt > 0 && !isCooldownElapsed(itemCooldown, itemShownAt, now)) {
    return false;
  }

  return true;
}

/**
 * Uses JST calendar days for cooldowns >= 1 day.
 */
function isCooldownElapsed(
  cooldown: number,
  shownAt: number,
  now: number,
): boolean {
  if (shownAt === 0) return true;

  const useJstDays = cooldown >= duration({ days: 1 });

  if (useJstDays) {
    const days = cooldown / duration({ days: 1 });
    const jstDaysDiff = getJSTDaysDiff(now, shownAt);
    return jstDaysDiff >= days;
  }

  return shownAt + cooldown <= now;
}
